import { cn } from "@/lib/utils"

type Tone = "alta" | "media" | "baja"

function toneOf(value: number): Tone {
  if (value >= 0.9) return "alta"
  if (value >= 0.75) return "media"
  return "baja"
}

const TONE_BAR: Record<Tone, string> = {
  alta: "bg-success",
  media: "bg-warning",
  baja: "bg-destructive",
}

const TONE_TEXT: Record<Tone, string> = {
  alta: "text-success",
  media: "text-warning",
  baja: "text-destructive",
}

const TONE_LABEL: Record<Tone, string> = {
  alta: "confianza alta",
  media: "confianza media, conviene mirar el fundamento",
  baja: "confianza baja, revisar contra el comprobante",
}

const SEGMENTS = 10

interface ConfidenceMeterProps {
  /** 0..1 */
  value: number
  className?: string
  /** Solo el instrumento, sin la cifra: para celdas muy estrechas. */
  compact?: boolean
}

/**
 * Confianza de la clasificacion como instrumento segmentado, no como barra
 * continua. Diez muescas se leen de un vistazo y a distancia: "siete de diez"
 * se percibe sin decodificar un ancho en pixeles, y el color (verde / ambar /
 * rojo) nunca es el unico portador del dato — al lado va el porcentaje.
 */
export function ConfidenceMeter({ value, className, compact }: ConfidenceMeterProps) {
  const clamped = Math.max(0, Math.min(1, value))
  const pct = Math.round(clamped * 100)
  const tone = toneOf(clamped)
  const filled = Math.max(1, Math.round(clamped * SEGMENTS))

  return (
    <div
      className={cn("flex items-center justify-end gap-1.5", className)}
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Confianza ${pct} por ciento: ${TONE_LABEL[tone]}`}
      title={`${pct}% — ${TONE_LABEL[tone]}`}
    >
      <div className="flex items-center gap-[1.5px]" aria-hidden="true">
        {Array.from({ length: SEGMENTS }).map((_, index) => (
          <span
            key={index}
            className={cn(
              "h-2.5 w-[3px] rounded-[1px] transition-colors",
              index < filled ? TONE_BAR[tone] : "bg-border-strong/50",
            )}
          />
        ))}
      </div>
      {compact ? null : (
        <span className={cn("w-8 text-right font-mono text-2xs font-semibold tabular-nums", TONE_TEXT[tone])}>
          {pct}%
        </span>
      )}
    </div>
  )
}

export { toneOf as confidenceTone }
